import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAppStore } from '../../store/appStore';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useTreatmentPdfExport } from './useTreatmentPdfExport';

/**
 * Export the saved treatment as a PDF. Frames are fetched and shrunk here, in
 * the browser, and sent with the request - see lib/treatmentPdf.js for why.
 */
export default function TreatmentPdfDialog({ open, onOpenChange, treatment, dirty }) {
  const { downloadTreatmentPdf } = useAppStore();
  const [includeEquipment, setIncludeEquipment] = useState(true);
  const { run: exportPdf, busy, percent, label } = useTreatmentPdfExport(treatment.shots, (images) =>
    downloadTreatmentPdf(treatment.id, { include_equipment: includeEquipment, images }),
  );
  const run = async () => {
    if (await exportPdf()) onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export PDF</DialogTitle>
          <DialogDescription>
            A4 landscape, shot by shot with its frames. Frames are loaded from where they're linked and placed in this PDF only.
          </DialogDescription>
        </DialogHeader>

        {dirty ? (
          <p className="text-sm rounded-cards bg-ochre-soft text-ochre px-3 py-2">Save your changes first - the PDF is made from the saved shot list.</p>
        ) : (
          <div className="space-y-4 py-1">
            <div className="flex items-center gap-2">
              <Checkbox id="pdf-equipment" checked={includeEquipment} onCheckedChange={(v) => setIncludeEquipment(!!v)} disabled={busy} />
              <Label htmlFor="pdf-equipment" className="font-normal">Include each shot's equipment (supplier names are never shown)</Label>
            </div>
            {busy && (
              <div className="space-y-2" aria-live="polite">
                <Progress value={percent} />
                <p className="text-sm text-muted-foreground">{label}</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={run} disabled={busy || dirty} data-testid="create-treatment-pdf">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Create PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
